import { useState } from "react";
import { useOpportunities } from "@/hooks/use-mock-data";
import { OpportunityCard } from "@/components/strangelove/OpportunityCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Search } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function OpportunitiesPage() {
  const [tierFilter, setTierFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [saturationFilter, setSaturationFilter] = useState("all");
  const [confidenceFilter, setConfidenceFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("score_desc");
  const [addOpen, setAddOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data: opportunities, isLoading, refetch } = useOpportunities({
    tier: tierFilter !== "all" ? tierFilter : undefined,
    status: statusFilter !== "all" ? statusFilter : undefined,
    saturation: saturationFilter !== "all" ? saturationFilter : undefined,
    confidence: confidenceFilter !== "all" ? confidenceFilter : undefined,
    sort: sortBy,
  });

  const filtered = searchQuery
    ? opportunities.filter((o) => {
        const q = searchQuery.toLowerCase();
        return (
          o.title.toLowerCase().includes(q) ||
          (o.opportunityName || "").toLowerCase().includes(q) ||
          (o.elevatorPitch || "").toLowerCase().includes(q) ||
          o.industry.toLowerCase().includes(q) ||
          o.sourceCommunity.toLowerCase().includes(q)
        );
      })
    : opportunities;

  return (
    <div className="animate-fade-in max-w-7xl mx-auto px-6 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-brand text-4xl tracking-wide">OPPORTUNITIES</h1>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="bg-electric-blue hover:bg-electric-blue/90 text-white text-xs uppercase tracking-wider">
              <Plus className="w-4 h-4 mr-1" /> Add Opportunity
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0C0C0C] border-[rgba(255,255,255,0.08)] max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-brand text-xl tracking-wide">ADD OPPORTUNITY</DialogTitle>
            </DialogHeader>
            <form className="space-y-4" onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              const title = (formData.get("title") as string)?.trim();
              if (!title) { toast.error("Title is required, Commander."); return; }
              setSubmitting(true);
              try {
                await api.post("/api/opportunities", {
                  title,
                  description: (formData.get("description") as string) || undefined,
                  sourceUrl: (formData.get("sourceUrl") as string) || undefined,
                  sourceCommunity: (formData.get("sourceCommunity") as string) || undefined,
                  industry: (formData.get("industry") as string) || undefined,
                  sourceType: (formData.get("sourceType") as string) || "manual",
                });
                toast.success("Opportunity acquired. Auto-scoring in progress, Commander.");
                setAddOpen(false);
                form.reset();
                refetch();
              } catch (err) {
                toast.error(err instanceof Error ? err.message : "Failed to add opportunity.");
              } finally {
                setSubmitting(false);
              }
            }}>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Title</Label>
                <Input name="title" className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] mt-1" placeholder="e.g., HVAC contractor scheduling chaos" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Description</Label>
                <Textarea name="description" className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] mt-1" rows={3} placeholder="Describe the pain signal..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Source URL</Label>
                  <Input name="sourceUrl" className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] mt-1" placeholder="https://..." />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Source Community</Label>
                  <Input name="sourceCommunity" className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] mt-1" placeholder="e.g., r/HVAC" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Industry</Label>
                  <Input name="industry" className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] mt-1" placeholder="e.g., Home Services" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Source Type</Label>
                  <Select name="sourceType" defaultValue="manual">
                    <SelectTrigger className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0C0C0C] border-[rgba(255,255,255,0.08)]">
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="pain_scanner">Pain Scanner</SelectItem>
                      <SelectItem value="builder_intel">Builder Intel</SelectItem>
                      <SelectItem value="trend_signal">Trend Signal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-electric-blue hover:bg-electric-blue/90 text-white font-semibold uppercase tracking-wider">
                {submitting ? "ACQUIRING TARGET..." : "ADD & AUTO-SCORE"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search opportunities..."
            className="pl-9 bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)]"
          />
        </div>
        <Select value={tierFilter} onValueChange={setTierFilter}>
          <SelectTrigger className="w-[150px] h-9 text-xs bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)]">
            <SelectValue placeholder="All Tiers" />
          </SelectTrigger>
          <SelectContent className="bg-[#0C0C0C] border-[rgba(255,255,255,0.08)]">
            <SelectItem value="all">All Tiers</SelectItem>
            <SelectItem value="strike_now">Strike Now</SelectItem>
            <SelectItem value="winner">Winner</SelectItem>
            <SelectItem value="watch_list">Watch List</SelectItem>
            <SelectItem value="parking_lot">Parking Lot</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] h-9 text-xs bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent className="bg-[#0C0C0C] border-[rgba(255,255,255,0.08)]">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="discovered">Discovered</SelectItem>
            <SelectItem value="deep_dive">Deep Dive</SelectItem>
            <SelectItem value="building">Building</SelectItem>
            <SelectItem value="launched">Launched</SelectItem>
          </SelectContent>
        </Select>
        <Select value={saturationFilter} onValueChange={setSaturationFilter}>
          <SelectTrigger className="w-[150px] h-9 text-xs bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)]">
            <SelectValue placeholder="Saturation" />
          </SelectTrigger>
          <SelectContent className="bg-[#0C0C0C] border-[rgba(255,255,255,0.08)]">
            <SelectItem value="all">All Saturation</SelectItem>
            <SelectItem value="blue_ocean">Blue Ocean</SelectItem>
            <SelectItem value="cracked_door">Cracked Door</SelectItem>
            <SelectItem value="red_ocean">Red Ocean</SelectItem>
          </SelectContent>
        </Select>
        <Select value={confidenceFilter} onValueChange={setConfidenceFilter}>
          <SelectTrigger className="w-[150px] h-9 text-xs bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)]">
            <SelectValue placeholder="Confidence" />
          </SelectTrigger>
          <SelectContent className="bg-[#0C0C0C] border-[rgba(255,255,255,0.08)]">
            <SelectItem value="all">All Confidence</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[150px] h-9 text-xs bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0C0C0C] border-[rgba(255,255,255,0.08)]">
            <SelectItem value="score_desc">Score (High to Low)</SelectItem>
            <SelectItem value="score_asc">Score (Low to High)</SelectItem>
            <SelectItem value="date_desc">Newest First</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 9 }).map((_, i) => (
            <Skeleton key={i} className="h-[120px] rounded-lg" />
          ))}
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((opp) => (
            <OpportunityCard key={opp.id} opportunity={opp} />
          ))}
        </div>
      ) : (
        /* Empty state */
        <div className="flex flex-col items-center justify-center py-20">
          <div className="relative mb-6">
            <div className="w-32 h-32 rounded-full border border-[rgba(255,255,255,0.06)]" />
            <div className="absolute inset-4 rounded-full border border-[rgba(255,255,255,0.05)]" />
            <div className="absolute inset-8 rounded-full border border-[rgba(255,255,255,0.04)]" />
            <div className="absolute inset-12 rounded-full border border-[rgba(255,255,255,0.03)]" />
          </div>
          <p className="text-muted-foreground text-sm text-center max-w-sm">
            The scanner detects nothing yet, Commander. Deploy Strangelove to begin.
          </p>
          <Button
            className="mt-4 bg-electric-blue hover:bg-electric-blue/90 text-white text-xs uppercase tracking-wider"
            onClick={() => setAddOpen(true)}
          >
            <Plus className="w-4 h-4 mr-1" /> Add Opportunity
          </Button>
        </div>
      )}
    </div>
  );
}
